/**
 * `/admin/users` — admin-user management list surface (M13, PRD 04
 * §Identity and access). Closes the "Admin-user management screen"
 * bullet on the M13 milestone.
 *
 * Every authenticated admin can manage admin accounts (the role-tier
 * model was retired in M15). The list mirrors the people-list pattern
 * (server-driven pagination, row click navigates to detail) and a
 * prominent **Add an admin** CTA drops an inline create form. On
 * successful create the SPA navigates straight to the new admin's
 * detail page so the actor can hand off the TOTP enrolment secret
 * without losing context.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import type { ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import type { AdminUserPublic, AdminUsersListResponse } from '@isms/shared';

export interface AdminUsersListPageProps {
  client: ApiClient;
  /** Where to send the admin on 401 / 403. Defaults to `/admin/sign-in`. */
  signInPath?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'ready'; data: AdminUsersListResponse };

type CreateStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'error'; error: ApiError | string };

const PAGE_SIZE = 25;

export function AdminUsersListPage({
  client,
  signInPath = '/admin/sign-in',
}: AdminUsersListPageProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [createStatus, setCreateStatus] = useState<CreateStatus>({ kind: 'idle' });
  const navigate = useNavigate();

  const redirectToSignIn = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate, signInPath]);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const data = await client.adminListUsers({ page, page_size: PAGE_SIZE });
      setState({ kind: 'ready', data });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 412)) {
        redirectToSignIn();
        return;
      }
      setState({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "We couldn't load the admin-user list. Please check your connection and try again.",
      });
    }
  }, [client, page, redirectToSignIn]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreateSubmit = useCallback(
    async (fields: { email: string; full_name: string }) => {
      setCreateStatus({ kind: 'pending' });
      try {
        const created = await client.adminCreateUser(fields);
        navigate(`/admin/users/${encodeURIComponent(String(created.admin_user_id))}`);
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 412)) {
          redirectToSignIn();
          return;
        }
        setCreateStatus({
          kind: 'error',
          error: err instanceof ApiError ? err : "We couldn't create that admin. Please try again.",
        });
      }
    },
    [client, navigate, redirectToSignIn],
  );

  return (
    <main className="admin-users" aria-labelledby="admin-users-heading">
      <nav className="admin-people-breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin">← Back to dashboard</Link>
      </nav>

      <header className="admin-users-header">
        <h1 id="admin-users-heading">Admin users</h1>
        <p className="admin-users-intro">
          Everyone who can sign in to the admin console. Deactivate an account by opening it and
          clearing the active toggle — the next sign-in attempt will be refused and any live session
          invalidated.
        </p>
      </header>

      <section className="admin-users-create" aria-labelledby="admin-users-create-heading">
        <header className="admin-users-create-header">
          <h2 id="admin-users-create-heading">Add an admin</h2>
          <button
            type="button"
            className="admin-users-create-toggle"
            onClick={() => {
              setShowCreate((v) => !v);
              setCreateStatus({ kind: 'idle' });
            }}
            aria-expanded={showCreate}
          >
            {showCreate ? 'Cancel' : 'Add an admin'}
          </button>
        </header>
        {showCreate ? (
          <CreateAdminForm
            status={createStatus}
            onSubmit={(fields) => void handleCreateSubmit(fields)}
          />
        ) : (
          <p className="admin-users-create-hint">
            Open the form to create a new admin account. The new admin receives a TOTP secret you
            can hand off on the detail page straight after creation.
          </p>
        )}
      </section>

      {state.kind === 'loading' ? (
        <p className="admin-people-loading" aria-busy="true">
          Loading admin users…
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <div className="admin-people-error">
          <ErrorBanner error={state.error} />
          <button type="button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <UsersList data={state.data} page={page} onPageChange={setPage} />
      ) : null}
    </main>
  );
}

interface CreateAdminFormProps {
  status: CreateStatus;
  onSubmit: (fields: { email: string; full_name: string }) => void;
}

function CreateAdminForm({ status, onSubmit }: CreateAdminFormProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');

  const pending = status.kind === 'pending';

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedName = fullName.trim();
    if (!trimmedEmail || !trimmedName) return;
    onSubmit({ email: trimmedEmail, full_name: trimmedName });
  }

  return (
    <form className="admin-users-create-form" onSubmit={handleSubmit} noValidate>
      <div className="admin-users-form-row">
        <label htmlFor="admin-users-create-email">Email</label>
        <input
          id="admin-users-create-email"
          type="email"
          autoComplete="off"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={pending}
        />
      </div>
      <div className="admin-users-form-row">
        <label htmlFor="admin-users-create-name">Full name</label>
        <input
          id="admin-users-create-name"
          type="text"
          autoComplete="off"
          required
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          disabled={pending}
        />
      </div>
      <p className="admin-users-form-hint">
        The new admin signs in at <code>/admin/sign-in</code> with the email above. Their first
        sign-in lands on the forced TOTP-enrolment screen — no password is set or shared anywhere.
      </p>
      <div className="admin-users-create-actions">
        <button type="submit" className="admin-exports-primary-button" disabled={pending}>
          {pending ? 'Creating…' : 'Create admin'}
        </button>
      </div>
      {status.kind === 'error' ? <ErrorBanner error={status.error} /> : null}
    </form>
  );
}

interface UsersListProps {
  data: AdminUsersListResponse;
  page: number;
  onPageChange: (page: number) => void;
}

function UsersList({ data, page, onPageChange }: UsersListProps): JSX.Element {
  const { items, pagination } = data;
  const hasPrev = pagination.page > 1;
  const hasNext = pagination.page < pagination.total_pages;
  return (
    <section className="admin-users-list-section" aria-labelledby="admin-users-list-heading">
      <header className="admin-users-list-header">
        <h2 id="admin-users-list-heading">Admins</h2>
        <span
          className="admin-users-list-count"
          aria-label={`${pagination.total_items} total admins`}
        >
          {pagination.total_items} total
        </span>
      </header>
      {items.length === 0 ? (
        <p className="admin-people-empty">No admin users yet. Use the form above to add one.</p>
      ) : (
        <ul className="admin-users-list">
          {items.map((user) => (
            <UserRow key={String(user.admin_user_id)} user={user} />
          ))}
        </ul>
      )}
      {pagination.total_pages > 1 ? (
        <nav className="admin-people-pagination" aria-label="Admin-user list pagination">
          <button type="button" disabled={!hasPrev} onClick={() => onPageChange(page - 1)}>
            ← Previous
          </button>
          <span aria-live="polite">
            Page {pagination.page} of {pagination.total_pages}
          </span>
          <button type="button" disabled={!hasNext} onClick={() => onPageChange(page + 1)}>
            Next →
          </button>
        </nav>
      ) : null}
    </section>
  );
}

interface UserRowProps {
  user: AdminUserPublic;
}

function UserRow({ user }: UserRowProps): JSX.Element {
  const id = String(user.admin_user_id);
  return (
    <li className="admin-users-row">
      <Link to={`/admin/users/${encodeURIComponent(id)}`} className="admin-users-row-link">
        <span className="admin-users-row-name">{user.full_name}</span>
        <span className="admin-users-row-email">{user.email}</span>
        <span className="admin-users-row-meta">
          <span
            className={`admin-people-status ${
              user.is_active ? 'admin-people-status-active' : 'admin-people-status-inactive'
            }`}
          >
            {user.is_active ? 'Active' : 'Deactivated'}
          </span>
          <span
            className={`admin-users-mfa-chip ${
              user.mfa_enrolled ? 'admin-users-mfa-enrolled' : 'admin-users-mfa-missing'
            }`}
          >
            {user.mfa_enrolled ? 'TOTP set' : 'TOTP missing'}
          </span>
        </span>
      </Link>
    </li>
  );
}
